import React from "react";
import {
    TaskStatus,
    TaskStatusTransition,
    TaskStatusTransitionVertex,
} from "../types";

export const STATUS_NODE_MIN_WIDTH = 228;
export const STATUS_NODE_MIN_HEIGHT = 104;
export const STATUS_NODE_VERTICAL_OFFSET = 110;

export const normalizeTransitionVertices = (
    vertices?: TaskStatusTransitionVertex[],
) =>
    vertices?.length
        ? vertices.map((vertex) => ({
              x: Math.round(vertex.x),
              y: Math.round(vertex.y),
          }))
        : undefined;

export const normalizeStatusDesign = (
    status: TaskStatus,
    index: number,
): NonNullable<TaskStatus["design"]> => ({
    width: Math.max(status.design?.width ?? 0, STATUS_NODE_MIN_WIDTH),
    height: Math.max(status.design?.height ?? 0, STATUS_NODE_MIN_HEIGHT),
    x: status.design?.x ?? 100,
    y: status.design?.y ?? 100 + STATUS_NODE_VERTICAL_OFFSET * index,
});

const normalizeStatuses = (taskStatuses: TaskStatus[]) =>
    taskStatuses.map((status, index) => ({
        id: status.id,
        isSubtaskStatus: status.isSubtaskStatus,
        isDefault: status.isDefault,
        isFinal: status.isFinal,
        design: normalizeStatusDesign(status, index),
        transitions: (status.transitions ?? [])
            .map((transition) => ({
                sourcePort: transition.sourcePort,
                targetPort: transition.targetPort,
                targetStatusId: transition.targetStatusId,
                vertices: normalizeTransitionVertices(transition.vertices),
            }))
            .sort((left, right) =>
                `${left.targetStatusId}:${left.sourcePort}:${left.targetPort}:${JSON.stringify(left.vertices ?? [])}`.localeCompare(
                    `${right.targetStatusId}:${right.sourcePort}:${right.targetPort}:${JSON.stringify(right.vertices ?? [])}`,
                ),
            ),
    }));

export const createWorkflowSnapshot = (taskStatuses: TaskStatus[]) => {
    return JSON.stringify({
        parent: normalizeStatuses(
            taskStatuses.filter((status) => !status.isSubtaskStatus),
        ),
        child: normalizeStatuses(
            taskStatuses.filter((status) => status.isSubtaskStatus),
        ),
    });
};

export const createAllowedActionsSnapshot = (taskStatuses: TaskStatus[]) => {
    const normalizeStatuses = (statuses: TaskStatus[]) =>
        statuses.map((status) => ({
            id: status.id,
            isSubtaskStatus: status.isSubtaskStatus,
            allowedActions: [...(status.allowedActions ?? [])].sort(),
        }));

    return JSON.stringify({
        parent: normalizeStatuses(
            taskStatuses.filter((status) => !status.isSubtaskStatus),
        ),
        child: normalizeStatuses(
            taskStatuses.filter((status) => status.isSubtaskStatus),
        ),
    });
};

const getStatusBadgeLabels = (status: TaskStatus) => {
    const labels: string[] = [];

    if (status.isDefault) {
        labels.push("Initial");
    }

    if (status.isFinal) {
        labels.push("Final");
    }

    return labels;
};

const getStatusActionsLabel = (status: TaskStatus) => {
    const count = status.allowedActions?.length ?? 0;

    if (count === 0) {
        return "No actions";
    }

    return `${count} action${count === 1 ? "" : "s"}`;
};

const StatusFlowNode: React.FC<{ status: TaskStatus }> = ({ status }) => {
    const badgeLabels = getStatusBadgeLabels(status);

    return (
        <div
            className={[
                "status-flow-node-shell",
                status.isDefault ? "status-flow-node-shell-default" : "",
                status.isFinal ? "status-flow-node-shell-final" : "",
                !status.isEnabled ? "status-flow-node-shell-disabled" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {status.isFinal && <div className="status-flow-node-final-rail" />}
            <div className="status-flow-node-content">
                <div className="status-flow-node-title">{status.name}</div>
                <div className="status-flow-node-tags">
                    {badgeLabels.map((label) => (
                        <div
                            key={label}
                            className="status-flow-node-tag status-flow-node-tag-meta"
                        >
                            {label}
                        </div>
                    ))}
                    <div className="status-flow-node-tag status-flow-node-tag-actions">
                        {getStatusActionsLabel(status)}
                    </div>
                </div>
            </div>
            {status.isFinal && <div className="status-flow-node-final-rail" />}
        </div>
    );
};

const getPortGroup = (position: string) => ({
    attrs: {
        circle: {
            fill: "#fff",
            magnet: true,
            r: 5,
            stroke: "#31d0c6",
            strokeWidth: 2,
            style: {
                visibility: "hidden",
            },
        },
    },
    position: {
        name: position,
    },
    zIndex: 10,
});

export const convertStatusToNode = (status: TaskStatus, index: number) => {
    const nextDesign = normalizeStatusDesign(status, index);

    if (
        !status.design ||
        status.design.width !== nextDesign.width ||
        status.design.height !== nextDesign.height ||
        status.design.x !== nextDesign.x ||
        status.design.y !== nextDesign.y
    ) {
        status.design = nextDesign;
    }

    const ports = {
        groups: {
            top: getPortGroup("top"),
            right: getPortGroup("right"),
            bottom: getPortGroup("bottom"),
            left: getPortGroup("left"),
        },
        items: [
            { group: "top", id: "Top" },
            { group: "right", id: "Right" },
            { group: "bottom", id: "Bottom" },
            { group: "left", id: "Left" },
        ],
    };

    const nodeConfig = {
        id: status.id,
        name: status.name,
        shape: "react-shape",
        component: <StatusFlowNode status={status} />,
        attrs: {
            body: {
                fill: "transparent",
                stroke: "none",
                refWidth: "100%",
                refHeight: "100%",
            },
            fo: {
                refWidth: "100%",
                refHeight: "100%",
            },
            label: {
                text: "",
            },
        },
        ports,
        ...nextDesign,
    };

    return {
        ...nodeConfig,
        data: {
            ...nodeConfig,
            group: false,
        },
    };
};

export const convertTransitionToEdge = (
    transition: TaskStatusTransition & { sourceStatusId: string },
) => ({
    id: `${transition.sourceStatusId}:${transition.sourcePort}-${transition.targetStatusId}:${transition.targetPort}`,
    attrs: {
        line: {
            stroke: "#A2B1C3",
            strokeWidth: 1,
        },
    },
    vertices: transition.vertices,
    source: {
        cell: transition.sourceStatusId,
        port: transition.sourcePort,
    },
    target: {
        cell: transition.targetStatusId,
        port: transition.targetPort,
    },
});
